import { Maybe, TowingOrderServiceType, TowingRecordPaymentType, TowingRecordServiceType } from '../../common/types'
import { Item, ItemGroup } from '../../containers/Parameters/types'
import { EditArrivalRouteFormValues } from '../../containers/TowingRecordPage/components/routes/subforms/ArrivalRoute/types'
import { EditFinishRouteFormValues } from '../../containers/TowingRecordPage/components/routes/subforms/FinishRoute/types'
import { TowingRecord } from '../../containers/TowingRecordPage/ViewTowingRecord/types'
import { ArcCodeList, ArcCodeLists, INVALID_ARC_DESCRIPTION_CODE } from './types'

export const getBatteryServiceOptions = (itemGroups: Maybe<ItemGroup[]>, items: Maybe<Item[]>) => {
  const options = [{ value: 'other', label: 'Muu' }]

  if (!itemGroups || !items) {
    return options
  }

  const batteryGroup = itemGroups.find(group => group.name === 'AL-Akkupalvelu')

  if (batteryGroup) {
    items.forEach(item => {
      if (item.groups.includes(batteryGroup.id)) {
        options.unshift({ value: item.name, label: item.name })
      }
    })
  }

  return options
}

export const getDeliveredBattery = (towingRecord: TowingRecord) => {
  return towingRecord.automobileAndTouringClubFinland &&
    towingRecord.automobileAndTouringClubFinland.batteryService &&
    towingRecord.automobileAndTouringClubFinland.batteryService.deliveredBattery
    ? towingRecord.automobileAndTouringClubFinland.batteryService.deliveredBattery
    : null
}

export const getBatteryServiceBattery = (
  itemGroups: Maybe<ItemGroup[]>,
  items: Maybe<Item[]>,
  battery: Maybe<string>
) => {
  const options = getBatteryServiceOptions(itemGroups, items)

  const batteryFound = options.find(option => option.value === battery)

  if (batteryFound) {
    return batteryFound.value
  }

  return 'other'
}

export const paymentTypeOptions = [
  { value: 'invoice', label: 'Lasku' },
  {
    value: TowingRecordPaymentType.card,
    label: 'Korttimaksu',
  },
  { value: TowingRecordPaymentType.cash, label: 'Käteismaksu' },
]

export function getAlArcOptions(componentCode: number, descriptionCode: number, arcCodelists: Maybe<ArcCodeLists>, serviceType: string) {

  if (!arcCodelists) {
      return { arcDescriptionOptions: [], arcComponentOptions: [], towingArcComponentOptions: [], roadServiceArcDescriptionOptions: [] }
  } 

  const components = getAvailableComponentsForServiceType(componentCode, serviceType, arcCodelists)
  const selectedComponent = components.find(component => Number(component.code) === componentCode)
  
  const availableDescriptionsForSelectedComponent = getAvailableDescriptionsForComponent(selectedComponent, descriptionCode, arcCodelists.descriptions )
 
  return {
    arcComponentOptions: components.map(component => {
      return {
        label: component.type,
        value: component.code,
      }
    }),
    arcDescriptionOptions: availableDescriptionsForSelectedComponent.map(description => {
      return {
        label: description.type,
        value: description.code,
      }
    }),
  }
}

function getAvailableComponentsForServiceType(componentCode: number, serviceType: string, arcCodelists: ArcCodeLists ) {
  let components = resolveComponentsForServiceType(serviceType, arcCodelists)

  const currentComponent = components.find(component => Number(component.code) === componentCode)

  if(!currentComponent) {
    const fallbackComponent = arcCodelists.components.find(component => Number(component.code) === componentCode)
    if(fallbackComponent) {
      components = [...components, fallbackComponent]
    }
  }

  return components
}

function getAvailableDescriptionsForComponent(selectedComponent: Maybe<ArcCodeList> | undefined, descriptionCode: number, descriptions: Omit<ArcCodeList, "fault_codes">[], ) {
  let result: Omit<ArcCodeList, 'fault_codes'>[] = []

  if(selectedComponent) {
    result = descriptions.filter(({ code }) => selectedComponent.fault_codes.includes(Number(code)))
  }

  //@Hack: If no valid fault codes in the component fault_codes, we still want to display the description for the driver
  const descriptionExists = descriptions.find(({ code } )=> Number(code) === descriptionCode)
  const isValidDescriptionCodeForComponent = result.find(({ code }) => Number(code) === descriptionCode)

  const invalidDescriptionCode = descriptionCode === INVALID_ARC_DESCRIPTION_CODE

  if(!isValidDescriptionCodeForComponent && descriptionExists && !invalidDescriptionCode) {
    result = [...result, { code: descriptionExists.code, type: descriptionExists.type }]
  }

  //Special ARC-code only assignable by Autoliitto operators
  if (invalidDescriptionCode) {
    result = [...result, { code: '46', type: '46 Ei diagnosoitavissa' }]
  }

  return result
}

function resolveComponentsForServiceType(serviceType: string, arcCodelists: ArcCodeLists) {
  console.log("[resolveComponentsForServiceType]: " + serviceType)
  const { components: allComponents, towing_components, road_service_components } = arcCodelists

  if(serviceType === TowingOrderServiceType.TOWING) {
    return towing_components
  }

  if(serviceType === TowingOrderServiceType.ROAD_SERVICE) {
    return road_service_components
  }

  return allComponents
}

interface ArcCodeValidationErrorProps {
  serviceType: Maybe<TowingRecordServiceType>,
  values: Maybe<TowingRecord | EditFinishRouteFormValues | EditArrivalRouteFormValues>
}

export const arcCodeValidationError = ({ serviceType, values }: ArcCodeValidationErrorProps, arcCodelists: Maybe<ArcCodeLists>) => {
  //should always be set
  if (!serviceType || !values || !values.automobileAndTouringClubFinland || !arcCodelists) return ''

  const descriptionCode = Number(values.automobileAndTouringClubFinland.arcCodes.description.code);
  const componentCode = Number(values.automobileAndTouringClubFinland.arcCodes.component.code);

  const componentsForServiceType = resolveComponentsForServiceType(serviceType, arcCodelists)
  const selectedComponentExistsForServiceType = componentsForServiceType.find(({ code} ) => Number(code) === componentCode)
 
  const { descriptions: allDescriptions, components: allComponents } = arcCodelists

  if(!selectedComponentExistsForServiceType) {
    const invalidComponentForServiceType = allComponents.find(({ code }) => Number(code) === componentCode)
    return `Virheellinen ARC-komponentti (${ invalidComponentForServiceType ? invalidComponentForServiceType.type : ""}) valitulle toimenpiteelle, valitse jokin toinen soveltuva komponentti.`
  }

  if (descriptionCode === INVALID_ARC_DESCRIPTION_CODE) {
    return 'Virheellinen ARC-vikakoodi (46, Ei diagnosoitavissa), valitse jokin toinen soveltuva vikakoodi.'
  }

  const descriptionCodeExistsForComponent = selectedComponentExistsForServiceType.fault_codes.includes(descriptionCode)
  if(!descriptionCodeExistsForComponent) {
     const invalidDescription = allDescriptions.find(({ code }) => Number(code) === descriptionCode)
     return `Virheellinen ARC-vikakoodi (${invalidDescription && invalidDescription.type}), valitse jokin toinen soveltuva vikakoodi.`
  }
}

export const getComponentType = (code: Maybe<string>, arcCodes: Maybe<ArcCodeLists>): string => {
  if (!arcCodes || !code) return ''
  const displayLabel = arcCodes.components.find(component => component.code === code)
  return displayLabel ? displayLabel.type : ''
}

export const getDescriptionType = (code: Maybe<string>, arcCodes: Maybe<ArcCodeLists>): string => {
  if (!arcCodes || !code) return ''
  const displayLabel = arcCodes.descriptions.find(description => description.code === code)
  return displayLabel ? displayLabel.type : ''
}
